import {
  BlockStack,
  Button,
  Checkbox,
  FormLayout,
  InlineGrid,
  InlineStack,
  LegacyStack,
  Modal,
  Tag,
  Text,
} from '@shopify/polaris'

import { DialogActivator } from '../common/helpers'
import { SellingPlanInterval } from '../common/plan-models'
import {
  IntervalCountField,
  IntervalField,
  MaxCyclesField,
  MinCyclesField,
} from '../common/ScheduleFields'
import { FinishingBehaviorSelect } from '../common/subscription-fields'
import { defaultFinishingBehavior, FinishingBehavior } from '../common/subscription-models'
import { PrePaidDeliveriesField } from '../selling-plan-page/PrePaidDeliveriesField'
import { anchorLabel } from '../subscription-page/ScheduleCard'
import {
  EditSchedulePayload,
  isEditScheduleFinishingEnabled,
  isEditScheduleFrequencyEnabled,
  isEditScheduleMinMaxEnabled,
} from './common'

interface Props {
  payload: EditSchedulePayload
  setPayload: (payload: EditSchedulePayload) => any
  editAnchorsActivator: DialogActivator
}

export const EditScheduleSection = ({ payload, setPayload, editAnchorsActivator }: Props) => {
  const frequencyEnabled = isEditScheduleFrequencyEnabled(payload)
  const minMaxEnabled = isEditScheduleMinMaxEnabled(payload)
  const finishingEnabled = isEditScheduleFinishingEnabled(payload)

  const interval = payload.interval ?? SellingPlanInterval.MONTH
  const deliveryIntervalCount = payload.deliveryIntervalCount ?? 1
  const prePaidDeliveries = payload.prePaidDeliveries ?? 1
  const anchors = payload.anchors ?? []

  const toggleFrequency = (checked: boolean) => {
    if (checked) {
      setPayload({
        ...payload,
        interval: SellingPlanInterval.MONTH,
        deliveryIntervalCount: 1,
        prePaidDeliveries: 1,
        anchors: [],
      })
    } else {
      setPayload({
        ...payload,
        interval: undefined,
        deliveryIntervalCount: undefined,
        prePaidDeliveries: undefined,
        anchors: undefined,
      })
    }
  }

  const toggleMinMax = (checked: boolean) => {
    if (checked) {
      setPayload({ ...payload, minMaxEnabled: true, minCycles: null, maxCycles: null })
    } else {
      setPayload({
        ...payload,
        minMaxEnabled: undefined,
        minCycles: undefined,
        maxCycles: undefined,
      })
    }
  }

  const toggleFinishing = (checked: boolean) => {
    setPayload({
      ...payload,
      finishingBehavior: checked ? defaultFinishingBehavior : undefined,
    })
  }

  return (
    <>
      <Modal.Section>
        <BlockStack gap="300">
          <Checkbox
            label="Update delivery frequency and anchors"
            checked={frequencyEnabled}
            onChange={toggleFrequency}
          />
          {frequencyEnabled && (
            <FormLayout>
              <InlineGrid columns={{ xs: 1, sm: 2 }} gap="300">
                <InlineStack blockAlign="end" gap="200">
                  <LegacyStack.Item fill>
                    <InlineStack align="space-evenly" gap="200">
                      {anchors.length <= 1 && (
                        <LegacyStack.Item fill>
                          <IntervalCountField
                            label="Delivery frequency"
                            value={deliveryIntervalCount}
                            onChange={(val) =>
                              setPayload({ ...payload, deliveryIntervalCount: val })
                            }
                          />
                        </LegacyStack.Item>
                      )}
                      <LegacyStack.Item fill>
                        <IntervalField
                          label={anchors.length > 1 ? 'Delivery frequency' : '  '}
                          value={interval}
                          onChange={(val: SellingPlanInterval) =>
                            setPayload({ ...payload, interval: val, anchors: [] })
                          }
                          hasMultipleAnchors={anchors.length > 1}
                        />
                      </LegacyStack.Item>
                    </InlineStack>
                  </LegacyStack.Item>
                </InlineStack>
                <PrePaidDeliveriesField
                  value={prePaidDeliveries}
                  onChange={(val) => setPayload({ ...payload, prePaidDeliveries: val })}
                  pagePath="/subscriptions"
                />
              </InlineGrid>
              {anchors.length === 0 && interval !== 'DAY' && (
                <Button onClick={editAnchorsActivator.show} variant="primary">
                  Add delivery anchors
                </Button>
              )}
              {anchors.length > 0 && (
                <BlockStack gap="300">
                  <InlineStack>
                    <LegacyStack.Item fill>
                      <Text as="span">Delivery anchors</Text>
                    </LegacyStack.Item>
                    <Button onClick={editAnchorsActivator.show} variant="plain">
                      Edit delivery anchors
                    </Button>
                  </InlineStack>
                  <InlineStack wrap gap="100">
                    {anchors.map((anchor, i) => (
                      <Tag key={i}>{anchorLabel(anchor)}</Tag>
                    ))}
                  </InlineStack>
                </BlockStack>
              )}
            </FormLayout>
          )}
        </BlockStack>
      </Modal.Section>

      <Modal.Section>
        <BlockStack gap="300">
          <Checkbox
            label="Update min/max number of payments"
            checked={minMaxEnabled}
            onChange={toggleMinMax}
          />
          {minMaxEnabled && (
            <InlineGrid columns={{ xs: 1, sm: 2 }} gap="300">
              <MinCyclesField
                value={payload.minCycles ?? null}
                onChange={(val) => setPayload({ ...payload, minCycles: val })}
              />
              <MaxCyclesField
                value={payload.maxCycles ?? null}
                onChange={(val) => setPayload({ ...payload, maxCycles: val })}
              />
            </InlineGrid>
          )}
        </BlockStack>
      </Modal.Section>

      <Modal.Section>
        <BlockStack gap="300">
          <Checkbox
            label="Update finishing behavior"
            checked={finishingEnabled}
            onChange={toggleFinishing}
          />
          {finishingEnabled && (
            <FinishingBehaviorSelect
              value={payload.finishingBehavior ?? defaultFinishingBehavior}
              onChange={(val: FinishingBehavior) =>
                setPayload({ ...payload, finishingBehavior: val })
              }
            />
          )}
        </BlockStack>
      </Modal.Section>
    </>
  )
}
